import React, { Component } from 'react';
import cx from 'classnames';
import { withFormik } from 'formik';
import { Button, Form, FormGroup, Label, Input } from 'reactstrap';
import { compose } from 'recompose';

import Error from 'shared/components/Error';
import Loading from 'shared/components/Loading';
import { State } from 'shared/models';

import MultiSelect from '../MultiSelect';
import { SUBSCRIBER_TYPES } from '../constants';
import styles from './CreateBathForm.styles';

class CreateBatchForm extends Component {
  getNameValue = () => {
    const { stateIds, utilityIds, type } = this.props.values;
    const states = stateIds ? stateIds.map(state => state.abbr) : [];
    const statesLabel = states && states.length ? states.join(', ') + ' - ' : '';
    let utilitiesLabel = '';

    if (utilityIds && utilityIds.length === 1) {
      utilitiesLabel = `${utilityIds.length} Utility - `;
    } else if (utilityIds && utilityIds.length > 1) {
      utilitiesLabel = `${utilityIds.length} Utilities - `;
    }

    const typeLabel = type
      ? SUBSCRIBER_TYPES.filter(subscriberType => subscriberType.value === parseFloat(type)).map(
          subscriberType => subscriberType.label
        )
      : 'All';

    return `${statesLabel}${utilitiesLabel}${typeLabel}`;
  };

  componentDidUpdate(prevProps) {
    const { values, setFieldValue } = this.props;

    if (prevProps.values.stateIds !== values.stateIds) {
      setFieldValue('name', this.getNameValue());
    }

    if (prevProps.values.utilityIds !== values.utilityIds) {
      setFieldValue('name', this.getNameValue());
    }

    if (prevProps.values.type !== values.type) {
      setFieldValue('name', this.getNameValue());
    }
  }

  render() {
    const {
      errors,
      handleBlur,
      handleChange,
      handleSubmit,
      isExpanded,
      isFetching,
      isSubmitting,
      onSelectBySubscriberClick,
      onToggle,
      serverError,
      setFieldValue,
      portfolios,
      projects,
      utilities,
      values
    } = this.props;

    const stateValues = values.stateIds ? values.stateIds.map(state => parseFloat(state.value)) : null;

    const utilityOptions = Object.keys(utilities.byId)
      .map(id => utilities.byId[id])
      .filter(utility => (values.stateIds && values.stateIds.length ? stateValues.includes(utility.stateId) : true))
      .map(({ name, id }) => ({
        label: name,
        value: id
      }));

    const portfolioOptions = Object.keys(portfolios.byId)
      .map(id => portfolios.byId[id])
      .filter(portfolio => {
        const { utilityIds, stateIds } = values;

        const hasUtilities = utilityIds && utilityIds.length;
        const hasStates = stateIds && stateIds.length;

        if (hasUtilities && hasStates) {
          return (
            portfolio.utilityIds?.some(uid => utilityIds.some(u => u.value === uid)) &&
            portfolio.stateIds?.some(sid => stateIds.some(state => parseInt(state.value) === sid))
          );
        }

        if (hasUtilities && !hasStates) {
          return portfolio.utilityIds?.some(uid => utilityIds.some(u => u.value === uid));
        }

        if (!hasUtilities && hasStates) {
          return portfolio.stateIds?.some(sid => stateIds.some(state => parseInt(state.value) === sid));
        }

        return true;
      })
      .map(({ name, id }) => ({
        label: name,
        value: id
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const projectOptions = Object.keys(projects.byId)
      .map(id => projects.byId[id])
      .filter(project => {
        const { portfolioIds, utilityIds, stateIds } = values;

        const hasPortfolios = portfolioIds && portfolioIds.length;
        const hasUtilities = utilityIds && utilityIds.length;
        const hasStates = stateIds && stateIds.length;

        if (hasPortfolios && hasUtilities && hasStates) {
          return (
            portfolioIds.some(p => p.value === project.portfolioId) &&
            utilityIds.some(u => u.value === project.utilityId) &&
            stateIds.some(s => parseInt(s.value) === project.stateId)
          );
        }

        if (!hasPortfolios && hasUtilities && hasStates) {
          return (
            utilityIds.some(u => u.value === project.utilityId) &&
            stateIds.some(s => parseInt(s.value) === project.stateId)
          );
        }

        if (!hasPortfolios && !hasUtilities && hasStates) {
          return stateIds.some(s => parseInt(s.value) === project.stateId);
        }

        if (!hasPortfolios && hasUtilities && !hasStates) {
          return utilityIds.some(u => u.value === project.utilityId);
        }

        if (hasPortfolios && !hasUtilities && hasStates) {
          return (
            portfolioIds.some(p => p.value === project.portfolioId) &&
            stateIds.some(s => parseInt(s.value) === project.stateId)
          );
        }

        if (hasPortfolios && hasUtilities && !hasStates) {
          return (
            portfolioIds.some(p => p.value === project.portfolioId) &&
            utilityIds.some(u => u.value === project.utilityId)
          );
        }

        if (hasPortfolios && !hasUtilities && !hasStates) {
          return portfolioIds.some(p => p.value === project.portfolioId);
        }

        return true;
      })
      .map(({ name, id }) => ({
        label: name,
        value: id
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const ServerError = () => {
      if (serverError && serverError.data !== 'There were no customers for the given filter criteria!') {
        return <Error className="my-2" error={serverError} />;
      }
      return null;
    };

    if (isFetching) return <Loading />;
    if (!isExpanded)
      return (
        <>
          <hr />
          <div className="px-4 mb-2">
            <Button block color="info" onClick={onToggle}>
              Create New Batch
            </Button>
          </div>

          <div className="px-4 mb-2">
            <Button block color="info" onClick={onSelectBySubscriberClick}>
              Select by Subscriber
            </Button>
          </div>
        </>
      );

    return (
      <div css={styles}>
        <hr />
        <div className="create-batch-form p-4 mb-2">
          <h5>
            <strong>Create a Batch</strong>
          </h5>
          <Form autoComplete="nope" onSubmit={handleSubmit}>
            <FormGroup>
              <Label for="state">State</Label>
              <MultiSelect
                disabled={isSubmitting}
                errors={errors}
                name="stateIds"
                onBlur={handleBlur}
                onChange={value => {
                  setFieldValue('utilityIds', []);
                  setFieldValue('portfolioIds', []);
                  setFieldValue('projectIds', []);
                  setFieldValue('stateIds', value);
                }}
                options={Object.keys(State).map(key => ({
                  label: State[key].name,
                  value: key,
                  abbr: State[key].abbr
                }))}
                placeholder="State"
                setFieldValue={setFieldValue}
                values={values}
              />
            </FormGroup>
            <FormGroup>
              <Label for="utility">Utility</Label>
              <MultiSelect
                disabled={isSubmitting}
                errors={errors}
                name="utilityIds"
                onBlur={handleBlur}
                onChange={value => {
                  setFieldValue('portfolioIds', []);
                  setFieldValue('projectIds', []);
                  setFieldValue('utilityIds', value);
                }}
                options={utilityOptions}
                placeholder="Utility"
                setFieldValue={setFieldValue}
                values={values}
              />
            </FormGroup>
            <FormGroup>
              <Label for="portfolioIds">Portfolio</Label>
              <MultiSelect
                disabled={isSubmitting}
                errors={errors}
                name="portfolioIds"
                onBlur={handleBlur}
                onChange={value => {
                  setFieldValue('projectIds', []);
                  setFieldValue('portfolioIds', value);
                }}
                options={portfolioOptions.map(type => ({
                  label: type.label,
                  value: type.value
                }))}
                placeholder="Portfolio"
                setFieldValue={setFieldValue}
                values={values}
              />
            </FormGroup>
            <FormGroup>
              <Label for="projectIds">Project</Label>
              <MultiSelect
                disabled={isSubmitting}
                errors={errors}
                name="projectIds"
                onBlur={handleBlur}
                onChange={value => {
                  setFieldValue('projectIds', value);
                }}
                options={projectOptions.map(type => ({
                  label: type.label,
                  value: type.value
                }))}
                placeholder="Project"
                setFieldValue={setFieldValue}
                values={values}
              />
            </FormGroup>
            <FormGroup>
              <Label for="type">Type</Label>
              <select
                className={cx('form-control', {
                  'is-invalid': errors['type']
                })}
                disabled={isSubmitting}
                errors={errors}
                name="type"
                onBlur={handleBlur}
                onChange={e => setFieldValue('type', e.target.value ? e.target.value : null)}
                placeholder="Type"
                values={values}
              >
                <option value="">All</option>
                {SUBSCRIBER_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </FormGroup>
            <FormGroup>
              <Label for="name">Batch Name</Label>
              <Input
                className="font-italic"
                disabled={isSubmitting}
                errors={errors}
                name="name"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="Name"
                value={values['name']}
              />
            </FormGroup>
            <Button block color="info" type="submit">
              Create Batch
            </Button>
            <ServerError />
            {errors.selectOne && <Error className="my-2">{errors.selectOne}</Error>}
          </Form>
          <Button block color="link" onClick={onToggle} size="sm">
            Cancel
          </Button>
        </div>
      </div>
    );
  }
}

export default compose(
  withFormik({
    mapPropsToValues: () => ({
      stateIds: [],
      utilityIds: [],
      type: '',
      name: '',
      portfolioIds: [],
      projectIds: []
    }),

    validate: values => {
      const errors = {};
      const { stateIds, utilityIds, type, portfolioIds, projectIds } = values;

      if (
        !stateIds.length &&
        !utilityIds.length &&
        type === '' &&
        portfolioIds.length === 0 &&
        projectIds.length === 0
      ) {
        errors.selectOne = 'Criteria must be selected to create a batch.';
      }

      return errors;
    },
    handleSubmit: (values, { props, resetForm, setSubmitting }) => {
      const { month: forMonth, year: forYear } = props;
      const { name, stateIds, utilityIds, type, portfolioIds, projectIds } = values;

      const formatFilters = () => {
        const filters = [
          ...stateIds,
          ...utilityIds,
          type ? { type, label: SUBSCRIBER_TYPES[type].label } : [],
          ...portfolioIds,
          ...projectIds
        ];

        const filterNames = filters.map(item => (item.abbr ? item.abbr : item.label)).join(':');
        return filterNames;
      };

      const filters = formatFilters();

      const data = {
        name,
        utilityIds: utilityIds ? utilityIds.map(u => u.value) : [],
        stateIds: stateIds ? stateIds.map(s => s.value) : [],
        type: type ? [type] : [],
        forMonth,
        forYear,
        portfolioIds: portfolioIds ? portfolioIds.map(item => item.value) : [],
        projectIds: projectIds ? projectIds.map(item => item.value) : [],
        filterNames: filters
      };

      props.onCreateFromFilters(data);
      setSubmitting(false);
      resetForm();
      props.onToggle(false);
    }
  })
)(CreateBatchForm);
